import React, { useCallback, useEffect, useState } from 'react';

import {
  ArrowLeftIcon,
  BookMarkedIcon,
  FlaskConicalIcon,
  HistoryIcon,
  InfoIcon,
  LightbulbIcon,
  MoreVerticalIcon,
  MessageCircleIcon,
  NetworkIcon,
  PencilIcon,
  RefreshCcwIcon,
  SettingsIcon,
  SparklesIcon,
  SquarePenIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import * as yup from 'yup';

import { brainsEndpoints } from '@/api/endpoints';
import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import AvatarGroup from '@/components/atoms/Avatar/AvatarGroup/AvatarGroup';
import { EditableText } from '@/components/atoms/EditableText/EditableText';
import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderTabs,
} from '@/components/organisms/PageSubHeader';
import { ResponsiveSidebarPanel } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import { AddMcpServerPanel } from '@/components/pages/MCPs/AddMcpServerPanel';
import { DeleteMcpServerDialog } from '@/components/pages/MCPs/DeleteMcpServerDialog';
import { EditMcpServerPanel } from '@/components/pages/MCPs/EditMcpServerPanel';
import useMcpServers, {
  type McpServer,
  type McpTool,
} from '@/components/pages/MCPs/hooks/useMcpServers';
import { getAuthHeaders, ResyncDialog } from '@/components/pages/MCPs/ResyncDialog';
import { WebhookSaveButton } from '@/components/pages/Webhook/WebhookSaveAction';
import { WebhookBreadcrumbName } from '@/components/pages/Webhooks/components/WebhookBreadcrumbName';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbLink,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ShortcutHint } from '@/components/ui/kbd';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SHORTCUTS } from '@/constants/shortcuts';
import useBrains from '@/hooks/useBrains';
import { useFromPageState } from '@/hooks/useFromPageState';
import { useSidebar } from '@/hooks/useSidebar';
import useUser from '@/hooks/useUser';
import { cn } from '@/lib/utils';
import { selectIsTryItModalOpen } from '@/redux/modals/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectOnlineViewersByGuidelinesId } from '@/redux/presence/selectors';
import { setBrainId } from '@/redux/session/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { noop } from '@/util/util';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { ImprovementDetails } from '../improvements/components/ImprovementDetails';
import { PendingImprovementsBadge } from '../improvements/components/PendingImprovementsBadge';
import { usePendingImprovementsCount } from '../improvements/hooks/useImprovements';
import { useSimulationsBreadcrumb } from '../simulations/SimulationsBreadcrumbContext';
import { HeaderActions } from './components/HeaderActions/HeaderActions';
import { NavigationSidebar } from './components/NavigationSidebar/NavigationSidebar';
import { AIAgentForm } from './components/RightPanel/AIAgentForm';
import { SpeechLanguageSidebar } from './components/SpeechStrategy/SpeechStrategySection';
import { useBillingState } from '../billing/hooks/useBillingState';
import { SidebarControls } from '../collections/hooks/useResizableSidebar';
import { CreateSimulationPanel } from '../simulations/components/List/CreateSimulationPanel';

const SIMULATIONS_RUN_DETAILS_PATTERN = /\/simulations\/runs\//;
// Any single path segment, so this stays in step with the `/mcps/:mcpId` route. A stricter
// UUID shape would silently drop the detail-page chrome (tabs reappear, breadcrumb and Re-sync
// vanish) for an id the router still renders — an uppercase UUID, for instance.
const MCP_DETAIL_PATTERN = /\/mcps\/([^/]+)/;
const WEBHOOK_DETAIL_PATTERN = /\/workflows\/webhooks\/([^/]+)/;

// Section pages replace the tab bar with a breadcrumb entry. One list feeds
// both the breadcrumb (getCurrentSection) and the restore-last-tab guard
// (isOnTabPage) so a new section can't be added to one and missed in the other.
const SECTION_ROUTES = [
  { path: '/settings', key: 'settings', labelKey: 'common.settings' },
  { path: '/versions', key: 'versions', labelKey: 'common.versions' },
  { path: '/insights', key: 'insights', labelKey: 'common.insights' },
  { path: '/mcps', key: 'mcps', labelKey: 'common.mcps' },
  { path: '/simulations', key: 'simulations', labelKey: 'simulations.title' },
  { path: '/improvements', key: 'improvements', labelKey: 'improvements.title' },
] as const;

// Collapse breadcrumb to its last level below 1024px (subheader width), matching the tabs.
const AI_AGENTS_BREADCRUMB_COLLAPSE = '@max-[1024px]:hidden';

type TabValue = 'overview' | 'guidelines' | 'workflows' | 'logs';

// Icon-only tabs below 1024px; measured on the subheader so the insights panel squeeze counts too.
const AI_AGENTS_TABS_ICON = 'hidden h-4 w-4 @max-[1024px]:block';
const AI_AGENTS_TABS_LABEL = '@max-[1024px]:hidden';

const AGENT_TABS = [
  {
    value: 'overview',
    icon: InfoIcon,
    labelKey: 'ai_agents.navigation.overview',
    shortcut: SHORTCUTS.goToOverview,
  },
  {
    value: 'guidelines',
    icon: BookMarkedIcon,
    labelKey: 'ai_agents.navigation.guidelines',
    shortcut: SHORTCUTS.goToGuidelines,
  },
  {
    value: 'workflows',
    icon: NetworkIcon,
    labelKey: 'ai_agents.navigation.workflows',
    shortcut: SHORTCUTS.goToWorkflows,
  },
  { value: 'logs', icon: MessageCircleIcon, labelKey: 'common.logs', shortcut: SHORTCUTS.goToLogs },
] as const;

interface SidebarProps {
  toggleSidebar: (sidebar: 'insights' | null) => void;
}

interface AIAgentLayoutProps {
  children: React.ReactNode;
  rightPanelContent?: React.ReactElement | null;
  showRightPanel?: boolean;
  sidebarControls?: SidebarControls<'insights'>;
}

export const AIAgentLayout: React.FC<AIAgentLayoutProps> = ({
  children,
  rightPanelContent,
  showRightPanel = false,
  sidebarControls,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    content: openSidebar,
    meta: sidebarMeta,
    open: openPanel,
    close: closeSidebar,
    isOpen,
  } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { brainId: brainIdFromUrl } = useParams<{ brainId: string }>();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const { brain, updateBrain, exportBrain } = useBrains(brainId);
  const pendingImprovementsCount = usePendingImprovementsCount(brainId ?? undefined);
  const [isRenameSidebarOpen, setIsRenameSidebarOpen] = React.useState(false);

  const { mcpServers, updateMcpServer, deleteMcpServer, testMutation } = useMcpServers(
    brainId ?? undefined
  );
  const [resyncServer, setResyncServer] = useState<McpServer | null>(null);
  const [deleteServer, setDeleteServer] = useState<McpServer | null>(null);
  const isMcpCreatePanelOpen = isOpen('create_mcp');
  const isMcpEditPanelOpen = isOpen('edit_mcp');
  const editServer = isMcpEditPanelOpen
    ? (mcpServers.find((s) => s.mcp_id === sidebarMeta?.mcpId) ?? null)
    : null;

  const handleResync = useCallback(
    (server: McpServer) => {
      testMutation.reset();
      setResyncServer(server);
      const headers = getAuthHeaders(server);
      testMutation.mutate({ url: server.url, headers });
    },
    [testMutation]
  );

  const handleResyncUpdate = useCallback(
    (tools: McpTool[]) => {
      if (!resyncServer) {
        return;
      }
      updateMcpServer({
        brain_id: resyncServer.brain_id,
        mcp_id: resyncServer.mcp_id,
        tools,
        tools_status: 'ok',
      });
      setResyncServer(null);
    },
    [resyncServer, updateMcpServer]
  );

  const handleResyncDialogChange = useCallback((open: boolean) => {
    if (!open) {
      setResyncServer(null);
    }
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteServer) {
      return;
    }
    deleteMcpServer(deleteServer.mcp_id);
    setDeleteServer(null);
    navigate(`/${slug}/ai-agents/${brainId}/mcps`);
  }, [brainId, deleteMcpServer, deleteServer, navigate, slug]);

  // Validation schema for brain name in breadcrumb
  const brainNameSchema = React.useMemo(() => {
    return yup
      .object()
      .shape({
        inputField: createNameFieldSchema({
          getEndpoint: brainsEndpoints.checkName,
          excludeName: brain?.name,
        }),
      });
  }, [brain?.name]);

  // Get the page number from router state to preserve pagination when navigating back
  const previousPageIndex = useFromPageState();

  /**
   * Save the current tab to localStorage for the given agent
   * Store the tab state when returning to an Agent
   * If they were on Versions/Insights/Settings do not store this, return them to overview
   */
  const setLastVisitedCategory = useCallback(
    (category: TabValue) => {
      const key = `aiAgents.lastVisitedCategory.${brainId}`;
      if (!brainId) {
        return;
      }
      try {
        // Only store valid tab values
        if (['overview', 'guidelines', 'workflows', 'logs'].includes(category)) {
          localStorage.setItem(key, category);
        }
      } catch (error) {
        console.error('Error saving last visited category to localStorage:', error);
      }
    },
    [brainId]
  );

  /**
   * Get the last visited tab from localStorage
   */
  const getLastVisitedCategory = useCallback((): TabValue | null => {
    const key = `aiAgents.lastVisitedCategory.${brainId}`;
    if (!brainId) {
      return null;
    }
    try {
      const stored = localStorage.getItem(key);
      if (stored && ['overview', 'guidelines', 'workflows', 'logs'].includes(stored)) {
        return stored as TabValue;
      }
    } catch (error) {
      console.error('Error reading last visited category from localStorage:', error);
    }
    return null;
  }, [brainId]);

  const isTryItModalOpen = useSelector(selectIsTryItModalOpen);

  // Keyboard shortcuts - disabled when form elements are focused or TryIt modal is open
  useHotkeys(
    SHORTCUTS.goToMcps,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/mcps`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.goToInsights,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/insights`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.goToVersions,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/versions`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.goToSettings,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/settings`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.goToOverview,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/overview`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.goToGuidelines,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/guidelines`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );
  useHotkeys(
    SHORTCUTS.goToWorkflows,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/workflows`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );
  useHotkeys(
    SHORTCUTS.goToLogs,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/logs`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );
  useHotkeys(
    SHORTCUTS.exportAgent,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      // Alt+E opens Chrome's menu by default; suppress it so only the export runs.
      e.preventDefault();
      exportBrain(brainId);
    },
    { enableOnFormTags: false },
    [brainId, exportBrain, isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.goToSimulations,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/simulations`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.goToImprovements,
    (e) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/improvements`);
    },
    { enableOnFormTags: false },
    [slug, brainId, isTryItModalOpen]
  );

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const { name } = brain || {};
  const { user: currentUser } = useUser();
  const { viewers: guidelinesViewers } = useSelector((state: RootState) =>
    selectOnlineViewersByGuidelinesId(state, brainId)
  );

  const guidelinesViewersWithoutMe = React.useMemo(
    () => guidelinesViewers?.filter(({ user_id }) => user_id !== currentUser?.user_id) ?? [],
    [currentUser?.user_id, guidelinesViewers]
  );

  // Get toggleSidebar function from sidebarControls
  const toggleSidebar = sidebarControls?.toggleSidebar;
  const isSidebarOpen = sidebarControls?.isSidebarOpen;

  // Clone children and pass toggleSidebar if needed
  const childrenWithProps =
    React.isValidElement(children) && toggleSidebar
      ? React.cloneElement(children as React.ReactElement<SidebarProps>, { toggleSidebar })
      : children;

  // Determine if we should show the read-only banner
  const shouldShowReadOnly =
    !location.pathname.includes('/workflows') &&
    !location.pathname.includes('/review') &&
    !location.pathname.includes('/versions') &&
    !canWrite;

  // Close the rename sidebar when AIAgentForm closes (openSidebar becomes null)
  React.useEffect(() => {
    if (openSidebar === null && isRenameSidebarOpen) {
      setIsRenameSidebarOpen(false);

      // Restore brainId if AIAgentForm cleared it (we're still on the brain page)
      // Only restore if it was cleared and we have a brainId from the URL
      if (brainId === null && brainIdFromUrl) {
        dispatch(setBrainId({ brainId: brainIdFromUrl }));
      }
    }
  }, [openSidebar, isRenameSidebarOpen, brainId, brainIdFromUrl, dispatch]);

  // Ensure only one sidebar is open at a time
  React.useEffect(() => {
    // If both sidebars are open, close rename (insights takes priority)
    if (isRenameSidebarOpen && isSidebarOpen) {
      setIsRenameSidebarOpen(false);
    }
  }, [isRenameSidebarOpen, isSidebarOpen]);

  // Restore last visited tab when returning to an agent
  // This runs once when the component mounts or when switching to a different agent
  useEffect(() => {
    if (!brainId || !slug) {
      return;
    }

    const pathname = location.pathname;
    const isOnVersions = pathname.includes('/versions');
    const isOnInsights = pathname.includes('/insights');
    const isOnSettings = pathname.includes('/settings') || pathname.includes('/advanced_settings');

    // Don't auto-redirect from these special pages
    if (isOnVersions || isOnInsights || isOnSettings) {
      return;
    }

    // Check if we're already on a specific tab or section page
    const isOnTabPage =
      pathname.includes('/overview') ||
      pathname.includes('/guidelines') ||
      pathname.includes('/workflows') ||
      pathname.includes('/logs') ||
      SECTION_ROUTES.some(({ path }) => pathname.includes(path));

    // Only restore if we're not already on a specific tab page
    if (!isOnTabPage) {
      const lastVisited = getLastVisitedCategory();
      const basePath = `/${slug}/ai-agents/${brainId}`;
      const navOptions = { replace: true };

      if (lastVisited) {
        switch (lastVisited) {
          case 'overview':
            navigate(`${basePath}/overview`, navOptions);
            break;
          case 'guidelines':
            navigate(`${basePath}/guidelines`, navOptions);
            break;
          case 'workflows':
            navigate(`${basePath}/workflows/dialogs`, navOptions);
            break;
          case 'logs':
            navigate(`${basePath}/logs`, navOptions);
            break;
        }
      } else {
        // No stored preference, default to overview
        navigate(`${basePath}/overview`, navOptions);
      }
    }
    // Only run when brainId changes (switching agents) or initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brainId]);

  const getActiveTab = (): TabValue => {
    const pathname = location.pathname;

    // Check tabs in order of specificity (more specific paths first)
    const tabRoutes: Array<{ path: string; tab: TabValue }> = [
      { path: '/guidelines', tab: 'guidelines' },
      { path: '/workflows', tab: 'workflows' },
      { path: '/logs', tab: 'logs' },
      { path: '/overview', tab: 'overview' },
    ];

    const matchedTab = tabRoutes.find(({ path }) => pathname.includes(path));
    return matchedTab?.tab ?? null;
  };

  const activeTab = getActiveTab();

  // Save the active tab whenever it changes (including keyboard shortcuts and direct navigation)
  useEffect(() => {
    if (activeTab) {
      setLastVisitedCategory(activeTab);
    }
  }, [activeTab, setLastVisitedCategory]);

  // When navigating to Versions/Insights/Settings, save 'overview' as last visited
  // So users return to overview when coming back
  useEffect(() => {
    const pathname = location.pathname;
    const isOnVersions = pathname.includes('/versions');
    const isOnInsights = pathname.includes('/insights');
    const isOnSettings = pathname.includes('/settings');

    if (isOnVersions || isOnInsights || isOnSettings) {
      setLastVisitedCategory('overview');
    }
  }, [location.pathname, setLastVisitedCategory]);

  // Get the current section name for breadcrumb
  const getCurrentSection = (): { name: string; key: string } | null => {
    const section = SECTION_ROUTES.find(({ path }) => location.pathname.includes(path));
    return section ? { name: t(section.labelKey), key: section.key } : null;
  };

  const currentSection = getCurrentSection();
  const { runLabel } = useSimulationsBreadcrumb();

  const webhookDetailId = WEBHOOK_DETAIL_PATTERN.exec(location.pathname)?.[1];
  const isWebhookStandalonePage = Boolean(webhookDetailId);
  const isWebhookCreatePage = webhookDetailId === 'new';

  // Check if we should show tabs (hide on versions/settings/insights)
  const shouldShowTabs = !currentSection && !isWebhookStandalonePage;

  // Check if we're on the MCPs page (needs its own tabs in header)
  const isMcpsPage = location.pathname.includes('/mcps');
  const mcpActiveTab =
    new URLSearchParams(location.search).get('tab') === 'local' ? 'local' : 'mcp';

  // A single MCP server's page. It counts as an MCP page for the main-content wrapper and for
  // the route-scoped sidebar guard (otherwise the Edit panel opened from here closes itself),
  // but it is not the list page, so the MCP/Local tabs are suppressed.
  const mcpDetailId = MCP_DETAIL_PATTERN.exec(location.pathname)?.[1];
  const isMcpDetailPage = Boolean(mcpDetailId);
  const isMcpsListPage = isMcpsPage && !isMcpDetailPage;
  const mcpDetailServer = mcpServers.find((server) => server.mcp_id === mcpDetailId) ?? null;

  // Check if we're on the Simulations page (needs its own tabs in header)
  const isSimulationsRunDetails = SIMULATIONS_RUN_DETAILS_PATTERN.test(location.pathname);
  const isSimulationsPage = location.pathname.includes('/simulations') && !isSimulationsRunDetails;
  // Simulations is gated behind a paid plan: on trial accounts the page shows a paywall, so
  // suppress the simulations sub-header tabs to avoid implying the feature is accessible.
  const isTrialPlan = useBillingState()?.plan_type === 'trial';
  const isSimulationsList =
    isSimulationsPage && !location.pathname.includes('/simulations/overview');
  const simulationsActiveTab = location.pathname.includes('/simulations/overview')
    ? 'overview'
    : 'simulations';

  // Where the header back arrow points, most specific route first.
  const getBackLink = () => {
    if (isWebhookStandalonePage) {
      return `/${slug}/ai-agents/${brainId}/workflows/webhooks`;
    }
    if (isSimulationsRunDetails) {
      return `/${slug}/ai-agents/${brainId}/simulations/overview`;
    }
    if (isMcpDetailPage) {
      return `/${slug}/ai-agents/${brainId}/mcps`;
    }
    if (currentSection) {
      return `/${slug}/ai-agents/${brainId}/overview`;
    }
    return `/${slug}/ai-agents`;
  };

  // Detail routes turn their section breadcrumb into a link back to the section's list.
  const getSectionBreadcrumbLink = () => {
    if (isSimulationsRunDetails) {
      return `/${slug}/ai-agents/${brainId}/simulations`;
    }
    if (isMcpDetailPage) {
      return `/${slug}/ai-agents/${brainId}/mcps`;
    }
    return null;
  };

  const sectionBreadcrumbLink = getSectionBreadcrumbLink();

  const showBrainAsLink = Boolean(currentSection) || isWebhookStandalonePage;

  // Route-scoped sidebars live in Redux (not the URL) while this layout is
  // shared across every agent subpath, so an open panel would otherwise persist
  // onto overview, guidelines, etc. Close whichever panel no longer matches its
  // page. Derive a single stable boolean so the effect only re-runs when the
  // condition flips, not on every unrelated sidebar content change (isOpen's
  // identity tracks content).
  const isImprovementsPage = location.pathname.includes('/improvements');
  const shouldCloseRouteScopedSidebar =
    (isOpen(['create_simulation', 'edit_simulation']) && !isSimulationsList) ||
    (isOpen(['create_mcp', 'edit_mcp']) && !isMcpsPage) || // includes the detail page on purpose
    (isOpen('improvement_details') && !isImprovementsPage);
  useEffect(() => {
    if (shouldCloseRouteScopedSidebar) {
      closeSidebar();
    }
  }, [shouldCloseRouteScopedSidebar, closeSidebar]);

  const handleMcpTabChange = (value: string) => {
    const next = new URLSearchParams(location.search);
    if (value === 'mcp') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    const qs = next.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  };

  const handleSimulationsTabChange = (value: string) => {
    const simPath = `/${slug}/ai-agents/${brainId}/simulations`;
    localStorage.setItem('simulations_last_tab', value);
    navigate(value === 'overview' ? `${simPath}/overview` : simPath, { replace: true });
  };

  // Check if we're on the overview page (it has its own custom layout)
  // Exclude /simulations/overview — that uses the simulations layout
  const isOverviewPage = location.pathname.includes('/overview') && !isSimulationsPage;

  // Check if we're on the guidelines page (needs centered layout)
  const isGuidelinesPage = location.pathname.includes('/guidelines');

  // Check if we're on the settings page (needs centered layout)
  const isSettingsPage = location.pathname.includes('/settings');

  // Check if we're on workflows (needs navigation sidebar)
  const isWorkflows = location.pathname.includes('/workflows') && !isWebhookStandalonePage;

  // Check if we're on the logs page (needs standard layout with responsive padding)
  const isLogsPage = location.pathname.includes('/logs');

  // Check if page needs no padding (versions, insights)
  const needsNoGutters =
    location.pathname.includes('/versions') || location.pathname.includes('/insights');

  const handleBrainNameSave = (newName: string) => {
    if (!brain || !newName.trim() || newName === brain.name) {
      return;
    }

    updateBrain({ brain_id: brain.brain_id, name: newName.trim() });
  };

  const handleTabChange = (value: string) => {
    const tab = value as TabValue;
    const basePath = `/${slug}/ai-agents/${brainId}`;

    // Save the tab state to localStorage
    setLastVisitedCategory(tab);

    switch (tab) {
      case 'overview':
        navigate(`${basePath}/overview`);
        break;
      case 'guidelines':
        navigate(`${basePath}/guidelines`);
        break;
      case 'workflows':
        navigate(`${basePath}/workflows/dialogs`);
        break;
      case 'logs':
        navigate(`${basePath}/logs`);
        break;
    }
  };

  const handleDropdownAction = (action: string) => {
    const basePath = `/${slug}/ai-agents/${brainId}`;

    switch (action) {
      case 'rename':
        // Close insights sidebar first if it's open
        if (isSidebarOpen && toggleSidebar) {
          toggleSidebar(null);
        }
        dispatch(setBrainId({ brainId }));
        openPanel('edit_ai_agent');
        setIsRenameSidebarOpen(true);
        break;
      case 'mcps':
        navigate(`${basePath}/mcps`);
        break;
      case 'insights':
        navigate(`${basePath}/insights`);
        break;
      case 'versions':
        navigate(`${basePath}/versions`);
        break;
      case 'settings':
        navigate(`${basePath}/settings`);
        break;
      case 'simulations':
        navigate(`${basePath}/simulations`);
        break;
      case 'improvements':
        navigate(`${basePath}/improvements`);
        break;
      case 'export':
        exportBrain(brainId);
        break;
    }
  };

  return (
    <SidebarLayout
      autoSaveId="ai-agent-sidebar"
      mainClassName="flex flex-col overflow-hidden"
      sidebar={
        <>
          {isRenameSidebarOpen && openSidebar === 'edit_ai_agent' && (
            <ResponsiveSidebarPanel
              title={t('ai_agents.rename_agent')}
              onClose={() => {
                setIsRenameSidebarOpen(false);
                closeSidebar();
              }}
            >
              <AIAgentForm />
            </ResponsiveSidebarPanel>
          )}

          {!isRenameSidebarOpen && (
            <Sidebar when="create_mcp" title={t('mcp.add_mcp_server')}>
              <AddMcpServerPanel />
            </Sidebar>
          )}

          {!isRenameSidebarOpen && editServer && (
            <Sidebar when="edit_mcp" title={t('mcp.edit_mcp_server')}>
              <EditMcpServerPanel
                key={editServer.mcp_id}
                server={editServer}
                onResync={handleResync}
              />
            </Sidebar>
          )}

          {!isRenameSidebarOpen && (
            <Sidebar
              when={['create_simulation', 'edit_simulation']}
              title={t(
                isOpen('edit_simulation')
                  ? 'simulations.edit_simulation'
                  : 'simulations.create_simulation'
              )}
            >
              <CreateSimulationPanel
                key={isOpen('edit_simulation') ? sidebarMeta?.simulationId : openSidebar}
                mode={isOpen('edit_simulation') ? 'edit' : 'create'}
              />
            </Sidebar>
          )}

          {!isRenameSidebarOpen && (
            <Sidebar when="improvement_details" title={t('improvements.details')}>
              <ImprovementDetails key={sidebarMeta?.improvementId} />
            </Sidebar>
          )}

          {!isRenameSidebarOpen && brainId && (
            <SpeechLanguageSidebar brainId={brainId} primaryLanguage={brain?.language} />
          )}

          {showRightPanel && isSidebarOpen && rightPanelContent && (
            <ResponsiveSidebarPanel
              order={3}
              title={t('insights.insights')}
              onClose={() => toggleSidebar?.(null)}
            >
              {rightPanelContent}
            </ResponsiveSidebarPanel>
          )}
        </>
      }
    >
      <PageSubHeader
        inlineTabs
        className="@container"
        breadcrumbs={
          // flex-1 fills the breadcrumb column so the name truncates instead of overflowing.
          <PageSubHeaderBreadcrumbs className="flex-1">
            <div className="flex items-center">
              {isMcpEditPanelOpen || isMcpCreatePanelOpen ? (
                <Button variant="ghost-neutral" size="icon-sm" onClick={closeSidebar}>
                  <ArrowLeftIcon size={16} />
                </Button>
              ) : (
                <Link
                  to={getBackLink()}
                  state={currentSection ? undefined : { fromPage: previousPageIndex + 1 }}
                >
                  <Button variant="ghost-neutral" size="icon-sm" onClick={noop}>
                    <ArrowLeftIcon size={16} />
                  </Button>
                </Link>
              )}
              <div className="mr-4 ml-2 h-4 w-px bg-border" />
              <Breadcrumb>
                <BreadcrumbList className="flex min-w-0 items-center">
                  {/* Level 1: AI Agents */}
                  <BreadcrumbItem className={cn('shrink-0', AI_AGENTS_BREADCRUMB_COLLAPSE)}>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/${slug}/ai-agents`}
                        state={{ fromPage: previousPageIndex + 1 }}
                        className="breadcrumb-link"
                      >
                        {t('common.ai_agents')}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={cn('shrink-0', AI_AGENTS_BREADCRUMB_COLLAPSE)} />

                  {/* Level 2: Brain name — hidden on narrow section pages, leaving only the section. */}
                  <BreadcrumbItem
                    className={cn(
                      'min-w-0 flex-1',
                      !showBrainAsLink && '-mx-1',
                      showBrainAsLink && AI_AGENTS_BREADCRUMB_COLLAPSE
                    )}
                  >
                    {showBrainAsLink ? (
                      <BreadcrumbLink asChild>
                        <Link
                          to={`/${slug}/ai-agents/${brainId}/overview`}
                          className="breadcrumb-link"
                        >
                          <span className="max-w-40 min-w-0 truncate">{name}</span>
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage className="min-w-0 gap-2 border-0 p-0">
                        {name ? (
                          <>
                            <EditableText
                              defaultValue={name}
                              onSubmit={handleBrainNameSave}
                              variant="body-regular"
                              color="var(--foreground)"
                              validationSchema={brainNameSchema}
                              overflowEllipsis
                              className="-mx-px"
                            />
                          </>
                        ) : (
                          <Skeleton className="h-4 w-30" />
                        )}
                      </BreadcrumbPage>
                    )}
                  </BreadcrumbItem>

                  {/* Level 3: Current section (Settings, Versions, Insights, MCPs, Simulations) */}
                  {currentSection && (
                    <>
                      <BreadcrumbSeparator
                        className={cn('shrink-0', AI_AGENTS_BREADCRUMB_COLLAPSE)}
                      />
                      <BreadcrumbItem className="shrink-0">
                        {sectionBreadcrumbLink ? (
                          <BreadcrumbLink asChild>
                            <Link to={sectionBreadcrumbLink} className="breadcrumb-link">
                              {currentSection.name}
                            </Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{currentSection.name}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </>
                  )}

                  {/* Level 4: Run details date */}
                  {isSimulationsRunDetails && runLabel && (
                    <>
                      <BreadcrumbSeparator className="shrink-0" />
                      <BreadcrumbItem className="shrink-0">
                        <BreadcrumbPage>{runLabel}</BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}

                  {/* Level 4: MCP server name, with its own actions menu */}
                  {isMcpDetailPage && (
                    <>
                      <BreadcrumbSeparator className="shrink-0" />
                      <BreadcrumbItem className="min-w-0">
                        {mcpDetailServer ? (
                          <BreadcrumbPage className="truncate">
                            {mcpDetailServer.name}
                          </BreadcrumbPage>
                        ) : (
                          <Skeleton className="h-4 w-30" />
                        )}
                      </BreadcrumbItem>
                      {mcpDetailServer && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost-neutral" size="icon-sm" className="h-6 w-6">
                              <MoreVerticalIcon size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="min-w-[192px]">
                            <DropdownMenuItem
                              className="gap-2"
                              disabled={!canWrite}
                              onSelect={() =>
                                openPanel('edit_mcp', { mcpId: mcpDetailServer.mcp_id })
                              }
                            >
                              <PencilIcon className="h-4 w-4 text-muted-foreground" />
                              <span>{t('common.edit')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="gap-2"
                              variant="destructive"
                              disabled={!canWrite}
                              onSelect={() => setDeleteServer(mcpDetailServer)}
                            >
                              <Trash2Icon className="h-4 w-4" />
                              <span>{t('common.delete')}</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </>
                  )}

                  {isWebhookStandalonePage && (
                    <>
                      <BreadcrumbSeparator className="shrink-0" />
                      <BreadcrumbItem className="min-w-0 gap-2">
                        {isWebhookCreatePage ? (
                          <BreadcrumbPage>{t('webhook.new_webhook')}</BreadcrumbPage>
                        ) : (
                          <WebhookBreadcrumbName
                            brainId={brainId ?? ''}
                            webhookId={webhookDetailId ?? ''}
                          />
                        )}
                      </BreadcrumbItem>
                    </>
                  )}

                  {/* Agent actions/navigation menu — shown only on the agent's tab pages
                      (Overview/Guidelines/Workflows/Logs). Hidden once you drill into a
                      section page (Settings/MCPs/Versions/Insights/Simulations), where the
                      section name appears in the breadcrumb instead. */}
                  {!currentSection && !isWebhookStandalonePage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost-neutral" size="icon-sm" className="h-6 w-6">
                          <MoreVerticalIcon size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="min-w-[192px]">
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('rename')}
                        >
                          <SquarePenIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('common.rename')}...</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('export')}
                        >
                          <UploadIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('common.export')}</span>
                          <ShortcutHint
                            shortcut={SHORTCUTS.exportAgent}
                            className="ml-auto"
                            kbdClassName="h-4 bg-background px-1 text-[8px]"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('mcps')}
                        >
                          <McpIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('common.mcps')}</span>
                          <ShortcutHint
                            shortcut={SHORTCUTS.goToMcps}
                            className="ml-auto"
                            kbdClassName="h-4 bg-background px-1 text-[8px]"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('simulations')}
                        >
                          <FlaskConicalIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('simulations.title')}</span>
                          <ShortcutHint
                            shortcut={SHORTCUTS.goToSimulations}
                            className="ml-auto"
                            kbdClassName="h-4 bg-background px-1 text-[8px]"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('improvements')}
                        >
                          <SparklesIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('improvements.title')}</span>
                          <PendingImprovementsBadge count={pendingImprovementsCount} />
                          <ShortcutHint
                            shortcut={SHORTCUTS.goToImprovements}
                            className="ml-auto"
                            kbdClassName="h-4 bg-background px-1 text-[8px]"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('insights')}
                        >
                          <LightbulbIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('common.insights')}</span>
                          <ShortcutHint
                            shortcut={SHORTCUTS.goToInsights}
                            className="ml-auto"
                            kbdClassName="h-4 bg-background px-1 text-[8px]"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('versions')}
                        >
                          <HistoryIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('common.versions')}</span>
                          <ShortcutHint
                            shortcut={SHORTCUTS.goToVersions}
                            className="ml-auto"
                            kbdClassName="h-4 bg-background px-1 text-[8px]"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onSelect={() => handleDropdownAction('settings')}
                        >
                          <SettingsIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{t('common.settings')}</span>
                          <ShortcutHint
                            shortcut={SHORTCUTS.goToSettings}
                            className="ml-auto"
                            kbdClassName="h-4 bg-background px-1 text-[8px]"
                          />
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {isGuidelinesPage && guidelinesViewersWithoutMe.length > 0 && (
                    <AvatarGroup>
                      {guidelinesViewersWithoutMe.map((viewer) => (
                        <Avatar
                          key={viewer.user_id}
                          userId={viewer.user_id}
                          size="small"
                          hideStatus
                        />
                      ))}
                    </AvatarGroup>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </PageSubHeaderBreadcrumbs>
        }
        tabs={
          shouldShowTabs ? (
            <PageSubHeaderTabs>
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-auto">
                <TabsList>
                  {AGENT_TABS.map(({ value, icon: Icon, labelKey, shortcut }) => (
                    <InfoTooltip
                      key={value}
                      variant="default"
                      side="top"
                      size="auto"
                      showArrow={false}
                      className="flex items-center gap-1.5"
                      content={
                        <>
                          {t('common.go_to')} {t(labelKey)}{' '}
                          <ShortcutHint shortcut={shortcut} kbdClassName="bg-background" />
                        </>
                      }
                    >
                      <span className="inline-flex">
                        <TabsTrigger value={value} aria-label={t(labelKey)} className="h-8">
                          <Icon className={AI_AGENTS_TABS_ICON} />
                          <span className={AI_AGENTS_TABS_LABEL}>{t(labelKey)}</span>
                        </TabsTrigger>
                      </span>
                    </InfoTooltip>
                  ))}
                </TabsList>
              </Tabs>
            </PageSubHeaderTabs>
          ) : isMcpsListPage ? (
            <PageSubHeaderTabs>
              <Tabs value={mcpActiveTab} onValueChange={handleMcpTabChange} className="w-auto">
                <TabsList>
                  <TabsTrigger value="mcp">{t('mcp.tab_title')}</TabsTrigger>
                  <TabsTrigger value="local">{t('local_tools.tab_title')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </PageSubHeaderTabs>
          ) : isSimulationsPage && !isTrialPlan ? (
            <PageSubHeaderTabs>
              <Tabs
                value={simulationsActiveTab}
                onValueChange={handleSimulationsTabChange}
                className="w-auto"
              >
                <TabsList>
                  <TabsTrigger value="simulations">{t('simulations.title')}</TabsTrigger>
                  <TabsTrigger value="overview">{t('simulations.runs')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </PageSubHeaderTabs>
          ) : null
        }
        actions={
          <PageSubHeaderActions>
            {/* The MCP server page has a single CTA in the design: re-sync this server. */}
            {isMcpDetailPage && mcpDetailServer && (
              <Button
                variant="outline-neutral"
                size="sm"
                disabled={!canWrite}
                onClick={() => handleResync(mcpDetailServer)}
              >
                <RefreshCcwIcon />
                {t('mcp.resync')}
              </Button>
            )}
            <WebhookSaveButton />
            {!isMcpDetailPage &&
              !isSimulationsPage &&
              !isSimulationsRunDetails &&
              !isWebhookStandalonePage && <HeaderActions />}
          </PageSubHeaderActions>
        }
      />

      {/* Main Content */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {shouldShowReadOnly && <ReadOnlyBanner />}
        <div key={activeTab} className="flex flex-1 overflow-hidden">
          {isOverviewPage ? (
            // Overview page handles its own layout with internal scrolling
            <div className="h-full w-full overflow-y-auto bg-subtle pb-6 lg:overflow-hidden lg:pb-0">
              {childrenWithProps}
            </div>
          ) : isWorkflows ? (
            // Workflows need navigation sidebar
            <div className="flex h-full w-full overflow-hidden bg-subtle">
              <NavigationSidebar />
              <div className="flex h-full flex-1 flex-col overflow-y-auto">{childrenWithProps}</div>
            </div>
          ) : isWebhookStandalonePage ? (
            <div className="flex h-full w-full flex-col overflow-y-auto bg-subtle">
              {childrenWithProps}
            </div>
          ) : needsNoGutters ? (
            // Versions, insights need no padding
            <div className="flex h-full w-full flex-col overflow-y-auto bg-subtle">
              {childrenWithProps}
            </div>
          ) : isGuidelinesPage ? (
            // Guidelines owns its own grid layout (rails + editor as siblings),
            // so this slot just provides the scroll container.
            <div className="h-full w-full overflow-y-auto bg-subtle">{childrenWithProps}</div>
          ) : isSettingsPage ? (
            // Settings page with proper scroll padding
            <div className="flex h-full w-full overflow-y-auto bg-subtle pt-4 pb-8">
              {childrenWithProps}
            </div>
          ) : isLogsPage ? (
            // Logs page with full-width layout
            <div className="h-full w-full bg-subtle px-4 pt-4 pb-6 lg:px-5 xl:px-6 2xl:px-8">
              {childrenWithProps}
            </div>
          ) : isMcpsPage ? (
            // MCPs page — inner containers (PageContentWrapper / tab content) own padding + scroll
            <div className="flex h-full w-full flex-col overflow-hidden bg-subtle">
              {childrenWithProps}
            </div>
          ) : isSimulationsPage || isSimulationsRunDetails || isImprovementsPage ? (
            // Simulations and improvements handle their own padding via TABLE_LAYOUT_FILL_CLASSES
            <div className="flex h-full w-full flex-col overflow-y-auto bg-subtle">
              {childrenWithProps}
            </div>
          ) : (
            // Default: other pages need full container structure
            <div className="flex h-full w-full flex-col overflow-y-auto bg-subtle px-9 py-6">
              {childrenWithProps}
            </div>
          )}
        </div>
      </main>

      <ResyncDialog
        open={!!resyncServer}
        onOpenChange={handleResyncDialogChange}
        server={resyncServer}
        testResult={testMutation.data}
        isLoading={testMutation.isPending}
        error={testMutation.error}
        onUpdate={handleResyncUpdate}
      />

      <DeleteMcpServerDialog
        server={deleteServer}
        onOpenChange={(open) => !open && setDeleteServer(null)}
        onConfirm={handleConfirmDelete}
      />
    </SidebarLayout>
  );
};
